// ProfilePage.tsx
import { useEffect, useRef } from "react";
import { useForm, Controller, FieldErrors } from "react-hook-form";
import { TextField, Button, MenuItem, Box } from "@mui/material";
import { useProfile, Profile } from "../../hooks/useProfile.tsx"; // 확장자 제거 권장
import ConsoleHeader from "../../components/common/ConsoleHeader.tsx";

const timezones = ["Asia/Seoul","Asia/Tokyo","America/Los_Angeles","Europe/London"];
const locales = ["ko-KR","en-US","ja-JP"];

export const EMPTY_PROFILE: Profile = {
  username: "",
  phone: "",
  department: "",
  position: "",
  timezone: "Asia/Seoul",
  locale: "ko-KR",
};

export default function ProfilePage() {
  const { load, save } = useProfile();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<Profile>({
    mode: "onBlur",
    defaultValues: EMPTY_PROFILE,
  });

  // ✅ mount 시 1회만 서버값을 불러오되,
  //    이미 사용자가 수정한 값(Dirty)은 유지한다.
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (loadedOnce.current) return;          // 한 번만
    loadedOnce.current = true;

    let active = true;                       // 언마운트/중복 호출 가드
    (async () => {
      try {
        const remote = await load();   
        console.log("load", remote)      // 서버 값
        if (!active) return;
        // 서버 값으로 reset하되, 사용자가 수정한 값은 덮어쓰지 않음
        reset({ ...EMPTY_PROFILE, ...remote }, { keepDirtyValues: true });
      } catch (e) {
        console.error("[/profile load] failed:", e);
      }
    })();

    return () => { active = false; };
  // deps를 비우면 ESLint가 경고할 수 있음. load/reset이 안정적이면 넣어도 OK.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: Profile) => {
    console.log("[submit data]", data);
    await save({ ...data, phone: normalizePhoneKR(data.phone || "") });
    alert("저장되었습니다");
  };
  const onInvalid = (errors: FieldErrors<Profile>) => {
    console.warn("[form invalid]", errors);
  };

  return (
    <Box>
      <ConsoleHeader
        code="MARS · PROFILE · OPERATOR"
        title="프로필"
        meta="계정에 등록된 운영자 정보를 확인하고 수정합니다."
      />
    <Box component="form"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      sx={{
        maxWidth: 560,
        mx: "auto",
        mt: 2,
        display: "grid",
        gap: 2,
        bgcolor: "#FFFFFF",
        border: "1px solid #E4E7EA",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        p: 3,
      }}
      noValidate
    >
      <Controller name="username" control={control}
        render={({ field }) => (
          <TextField {...field} value={field.value ?? ""} label="이름" inputProps={{ autoComplete: "name" }} />
        )}/>

      <Controller name="phone" control={control}
        rules={{ pattern: { value: /^[0-9\-+() ]*$/, message: "숫자/하이픈만" } }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value ?? ""} label="전화번호"
            error={!!fieldState.error} helperText={fieldState.error?.message}
            onChange={(e)=>field.onChange(formatPhoneKR(e.target.value))}
            inputProps={{ inputMode: "tel", autoComplete: "tel" }}
          />
        )}/>

      <Controller name="department" control={control}
        render={({ field }) => (<TextField {...field} value={field.value ?? ""} label="부서" />)}/>

      <Controller name="position" control={control}
        render={({ field }) => (<TextField {...field} value={field.value ?? ""} label="직책" />)}/>

      <Button variant="contained" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "저장 중..." : "저장"}
      </Button>
    </Box>
    </Box>
  );
}

function onlyDigits(s: string){ return s.replace(/\D/g, ""); }
function formatPhoneKR(input: string){
  const d = onlyDigits(input);
  if (d.startsWith("02")) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return d.slice(0,2)+"-"+d.slice(2);
    if (d.length <= 9) return d.slice(0,2)+"-"+d.slice(2,5)+"-"+d.slice(5);
    return d.slice(0,2)+"-"+d.slice(2,6)+"-"+d.slice(6,10);
  } else {
    if (d.length <= 3) return d;
    if (d.length <= 7) return d.slice(0,3)+"-"+d.slice(3);
    return d.slice(0,3)+"-"+d.slice(3,7)+"-"+d.slice(7,11);
  }
}
function normalizePhoneKR(input: string){ return formatPhoneKR(input); }
